// CP Site Visit modal — a trimmed variant of AddSiteVisit for the CP Sourcing tab.
// Step 0: CP Name (read-only), Site Visit On, CP Site Visit Photo, Site Visit Remarks.
// Step 1: Phone (pre-filled from CP) + OTP verification, Start/End time.
// PAN verification is intentionally removed (kycOption is always false here).
// NOTE: frontend-only — final Save is stubbed; backend wiring comes later.
import React, { useState, useEffect, useMemo } from "react";
//*MUI
import { Box, Modal, TextField, InputAdornment } from "@mui/material";
import MobileStepper from "@mui/material/MobileStepper";
import ArrowBack from "@mui/icons-material/ArrowBack";
//*react-phone-number-input
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
//*files — reuse the existing Site Visit modal styles + shared subcomponents
import cssClasses from "../AddSiteVisit/AddSiteVisit.module.css";
import Close from "../../Components/Icons/Close";
import Button from "../../Subcomponents/Button";
import CustomInput from "../../Subcomponents/CustomInput";
import CameraCapture from "../../Subcomponents/CameraCapture";
import DateComponent from "../../Subcomponents/DateComponent";
import CountdownTimer from "../../Subcomponents/CountDownTimer";
import { checkPhoneNumberIsBlackListed } from "../../Services/Lead";
import { sendOTPMessage } from "../../Services/SmsService";
import { errorTost, successTost } from "../../Services/Toast";
import SearchSelect from "./SearchSelect";
import {
  addCpSiteVisit,
  getPublishedProjects,
} from "../../Services/CpSourcingApis";

// Stored verbatim in cpSiteVisits.mode (free-text column).
const MODE_OPTIONS = ["CP Office", "VJ Office", "Online"].map((m) => ({
  label: m,
  value: m,
}));

const style = {
  position: "absolute",
  top: "45%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  height: "80%",
  // Fixed width so the modal does NOT resize between Step 0 and Step 1
  // (Fit-Content made each step shrink to its own content and jump sizes).
  width: "46rem",
  maxWidth: "92vw",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
};

// Time-only picker — clock only, no calendar; caller fixes the date to "today".
// (Kept local, mirrors the EndTimePicker used in AddSiteVisit.)
function EndTimePicker({ value, helperText, onChangeTime }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MobileTimePicker
        value={value ? dayjs(value) : null}
        onChange={(e) => (e ? onChangeTime(e["$d"]) : onChangeTime(""))}
        inputFormat="L HH:mm"
        PopperProps={{ placement: "bottom-start" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <CalendarTodayOutlinedIcon
                style={{
                  fontSize: "2rem",
                  marginLeft: "1.5rem",
                  color: "#667085",
                  cursor: "pointer",
                }}
              />
            </InputAdornment>
          ),
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            placeholder="HH:MM"
            helperText={helperText}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#F2F4F7",
                borderRadius: "0.8rem",
                fontSize: "1.4rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border:
                  helperText !== ""
                    ? "1px solid red !important"
                    : "1px solid #F2F4F7 !important",
              },
              "& input": {
                padding: "1rem 1.4rem",
                fontSize: "1.4rem",
                color: "#667085",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "1.4rem",
                margin: "0.2rem 0 0 0",
                color: "#d32f2f",
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
}

// Normalize the CP phone (which may arrive as "9876543210", "919876543210",
// or "+91…") into an E.164-ish value the PhoneInput accepts.
function normalizePhone(raw) {
  if (!raw) return "";
  let p = String(raw).replace(/\s+/g, "");
  if (p.startsWith("+")) return p;
  if (p.length === 12 && p.startsWith("91")) return "+" + p;
  if (p.length === 10) return "+91" + p;
  return "+" + p;
}

function AddCpSiteVisit({ open, handleClose, cp }) {
  const loggedInUser = useSelector((state) => state.Auth.user);

  const [activeStep, setActiveStep] = useState(0);

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [projectError, setProjectError] = useState("");
  const [selectedMode, setSelectedMode] = useState("");
  const [modeError, setModeError] = useState("");

  //*Photo
  const [webCamModalToggler, setWebCamModalToggler] = useState(false);
  const [imageBase64, setImageBase64] = useState(null);

  //*Step 0 fields
  const [dateTime, setDateTime] = useState(dayjs(Date.now()));
  const [remark, setRemark] = useState("");

  //*Phone + OTP
  const [contactNumber, setContactNumber] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [OtpNumber, setOtpNumber] = useState({
    otp1: "",
    otp2: "",
    otp3: "",
    otp4: "",
  });
  const [recievedOtp, setRecievedOtp] = useState(null);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(false);
  const [resendButton, setResendButton] = useState(false);
  const [otptimer, setOtpTimer] = useState(false);
  const [verified, setVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [helperPhone, setHelperPhone] = useState("");

  //*Start/End time (default today at 00:00 — pickers change only the time)
  const [siteVisitStart, setSiteVisitStart] = useState(() =>
    dayjs().startOf("day").toDate(),
  );
  const [siteVisitEnd, setSiteVisitEnd] = useState(() =>
    dayjs().startOf("day").toDate(),
  );
  const [dateTimeError, setDateTimeError] = useState({ start: "", end: "" });

  // Reset + pre-fill whenever the modal is (re)opened for a CP.
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setImageBase64(null);
      setDateTime(dayjs(Date.now()));
      setRemark("");
      setContactNumber(normalizePhone(cp?.phone));
      setShowOtp(false);
      setOtpNumber({ otp1: "", otp2: "", otp3: "", otp4: "" });
      setRecievedOtp(null);
      setVerifiedPhoneNumber(false);
      setResendButton(false);
      setOtpTimer(false);
      setVerified(false);
      setHelperPhone("");
      setSubmitting(false);
      setSiteVisitStart(dayjs().startOf("day").toDate());
      setSiteVisitEnd(dayjs().startOf("day").toDate());
      setDateTimeError({ start: "", end: "" });
      setSelectedProject("");
      setProjectError("");
      setSelectedMode("");
      setModeError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cp?.cpId]);

  useEffect(() => {
    if (!open) return;

    const fetchProjects = async () => {
      try {
        const data = await getPublishedProjects();
        setProjects(data);
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    };

    fetchProjects();
  }, [open]);

  const projectOptions = useMemo(
    () =>
      projects.map((project) => ({
        label: project.projectName,
        value: project.projectId,
      })),
    [projects],
  );

  // Allow both Capture and Upload for every CP site visit (the modal is only reachable
  // by CpSourcing users, so no extra Leads/Sustenance/Event permission is required).
  const uploadPhotoAccess = true;

  function goBack() {
    setActiveStep((prev) => prev - 1);
  }
  function handleNext() {
    setActiveStep((prev) => prev + 1);
  }

  function CancelClose() {
    handleClose();
    setActiveStep(0);
    setSiteVisitStart(null);
    setSiteVisitEnd(null);
    setDateTimeError({ start: "", end: "" });
  }

  function SavingImageData() {
    setWebCamModalToggler(false);
  }

  //*Every required Step 0 field, checked in the order they appear on screen so
  //*the toast names the FIRST thing missing. Returns that message, or null when
  //*the step is complete. validateProject/validateMode also set the inline
  //*helper text, so the offending field is highlighted alongside the toast.
  function firstStepZeroError() {
    if (!validateProject()) return "Please select a project";
    if (imageBase64 === null) return "Please Capture Photo";
    if (!validateMode()) return "Please select a mode";
    return null;
  }

  //*Step 0 → Step 1
  function NextStep() {
    const problem = firstStepZeroError();
    if (problem) {
      errorTost(problem);
      return;
    }
    handleNext();
  }

  //*phone blacklist check (debounced)
  const IsNumberBlackListed = debounce((phone) => {
    checkPhoneNumberIsBlackListed(phone)
      .then((resp) => {
        if (resp.length > 0) {
          setVerifiedPhoneNumber(false);
          setHelperPhone("This number has been blacklisted");
        } else {
          setHelperPhone("");
        }
      })
      .catch((err) => console.log(err));
  }, 500);

  //*OTP box focus + change
  const inputfocus = (elmnt) => {
    if (elmnt.key === "Delete" || elmnt.key === "Backspace") {
      // no-op
    } else {
      const next = parseInt(elmnt.target.id.split("-")[1]) + 1;
      if (next < 5) {
        if (elmnt.target.nextSibling) elmnt.target.nextSibling.focus();
      }
    }
  };
  function optChange(key, event) {
    event.preventDefault();
    setOtpNumber((pre) => ({ ...pre, [key]: event.target.value }));
  }

  //*if-otp-expired
  function smsTimeOut() {
    setHelperPhone("Otp has expired.Please resend otp");
    setShowOtp(true);
    setVerifiedPhoneNumber(false);
    setOtpTimer(true);
    setOtpNumber({ otp1: "", otp2: "", otp3: "", otp4: "" });
    setRecievedOtp(null);
  }

  //*send OTP
  function smsApiCall() {
    setResendButton(false);
    setOtpNumber({ otp1: "", otp2: "", otp3: "", otp4: "" });

    const activePhone = contactNumber;
    const isInternationalNumber = !activePhone?.startsWith("+91");
    setShowOtp(true);

    const body = {
      number: activePhone,
      isInterNational: isInternationalNumber,
      email: undefined,
    };

    sendOTPMessage(body)
      .then((resp) => {
        if (resp.data) {
          const encryptedBase64 = resp.data.otp;
          const encrypted = atob(encryptedBase64);
          const bytes = CryptoJS.AES.decrypt(encrypted, "hts secret key");
          const originalText = bytes.toString(CryptoJS.enc.Utf8);

          if (!originalText) {
            errorTost("OTP decryption failed, please try again.");
            return;
          }
          setRecievedOtp(parseInt(originalText));
          setTimeout(smsTimeOut, 900000);

          if (!resp.data.success && !isInternationalNumber) {
            if (resp?.data?.error?.ErrorMessage) {
              errorTost(resp.data.error.ErrorMessage);
            } else {
              errorTost("Something went wrong while sending SMS");
            }
          }
        }
      })
      .catch((err) => {
        console.error("sendOTPMessage error:", err.message);
        errorTost("Failed to send OTP. Please try again.");
      });
  }

  //*verify OTP — no PAN step here, so a matching OTP verifies the whole flow.
  function submitOtp() {
    const finalValue =
      OtpNumber.otp1 + OtpNumber.otp2 + OtpNumber.otp3 + OtpNumber.otp4;
    if (+finalValue === recievedOtp) {
      setVerifiedPhoneNumber(true);
      setShowOtp(false);
      setVerified(true);
    } else {
      setOtpNumber({ otp1: "", otp2: "", otp3: "", otp4: "" });
      errorTost("OTP doesn't match.Please enter valid otp");
    }
  }

  //*Validate Project: required
  function validateProject() {
    if (!selectedProject) {
      setProjectError("Please select a project");
      return false;
    }
    setProjectError("");
    return true;
  }

  // **Validate Start/End time: both required, End must be after Start
  //*Validate Mode: required
  function validateMode() {
    if (!selectedMode) {
      setModeError("Please select a mode");
      return false;
    }
    setModeError("");
    return true;
  }

  function validateSiteVisitDateTimes() {
    let valid = true;
    let errors = { start: "", end: "" };
    if (!siteVisitStart) {
      errors.start = "Please select site visit start date & time";
      valid = false;
    }
    if (!siteVisitEnd) {
      errors.end = "Please select site visit end date & time";
      valid = false;
    }
    if (
      siteVisitStart &&
      siteVisitEnd &&
      new Date(siteVisitEnd).getTime() <= new Date(siteVisitStart).getTime()
    ) {
      errors.end = "End date & time must be after start date & time";
      valid = false;
    }
    setDateTimeError(errors);
    return valid;
  }

  //*Save — store the CP site visit via the backend cp-sourcing API.
  async function OnSubmit() {
    const stepZeroProblem = firstStepZeroError();
    if (stepZeroProblem) {
      errorTost(stepZeroProblem);
      setActiveStep(0); // the offending field lives on Step 0 — show it
      return;
    }
    if (!validateSiteVisitDateTimes()) return;
    if (submitting) return;

    const userId = loggedInUser?.userId || loggedInUser?.id;
    if (!userId) {
      errorTost("Unable to identify the logged-in user.");
      return;
    }

    const payload = {
      userId,
      projectId: selectedProject,
      mode: selectedMode,
      contactNumber,
      remark,
      dateTime: dateTime ? Number(dateTime) : null,
      startTime: siteVisitStart ? new Date(siteVisitStart).getTime() : null,
      endTime: siteVisitEnd ? new Date(siteVisitEnd).getTime() : null,
      photoBase64: imageBase64,
    };

    try {
      setSubmitting(true);
      const res = await addCpSiteVisit(cp?.cpId, payload);
      if (res?.success) {
        successTost("CP Site Visit saved successfully.");
        CancelClose();
      } else {
        errorTost(res?.message || "Failed to save CP Site Visit.");
      }
    } catch (err) {
      console.error("addCpSiteVisit error:", err);
      errorTost(
        err?.response?.data?.message || "Failed to save CP Site Visit.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
      >
        <Box sx={style}>
          <Box className={cssClasses.headerDiv}>
            <Box className={cssClasses.flexRow}>
              <Box className={cssClasses.flexCol}>
                <Box className={cssClasses.HeaderTitle} mt={1}>
                  Add CP Site Visit
                </Box>{" "}
              </Box>
              <Box
                ml={"auto"}
                mt={1}
                onClick={CancelClose}
                style={{ cursor: "pointer" }}
              >
                <Close width={"1.2rem"} height={"1.2rem"} />
              </Box>
            </Box>
          </Box>

          <MobileStepper
            variant=""
            steps={1}
            position="static"
            activeStep={activeStep}
          />

          <Box width={"100%"} height={"80%"} overflow={"auto"}>
            <Box margin={"1.5rem 3.2rem"}>
              {/* ---------------- STEP 0 ---------------- */}
              {activeStep === 0 && (
                <Box>
                  <Box
                    className={cssClasses.title}
                    style={{ marginBottom: "1.8rem" }}
                  >
                    Site Visit Details
                  </Box>

                  {/* CP Name — read-only / frozen */}
                  <Box margin={"2rem 0"}>
                    <Box className={cssClasses.InputTitle} mb={1}>
                      CP Name
                    </Box>
                    <CustomInput
                      inputType={"Disabled"}
                      disabled={true}
                      value={cp?.cpName || "—"}
                      handleChange={() => {}}
                      placeholder={"CP Name"}
                    />
                  </Box>

                  {/* Project — the site visit is recorded against a live project */}
                  <Box margin={"2rem 0"}>
                    <Box className={cssClasses.InputTitle} mb={1}>
                      Select Project <span className={cssClasses.Star}>*</span>
                    </Box>
                    <SearchSelect
                      options={projectOptions}
                      value={selectedProject}
                      onChange={(value) => {
                        setSelectedProject(value);
                        setProjectError("");
                      }}
                      placeholder="Select Project"
                      helperText={projectError}
                      searchable
                      searchPlaceholder="Search project"
                      emptyText="No projects found"
                    />
                  </Box>

                  {/* Site Visit On */}
                  <Box margin={"2rem 0"}>
                    <Box className={cssClasses.InputTitle}>
                      Site Visit On
                      <span className={cssClasses.Star}>*</span>
                    </Box>
                    <DateComponent
                      value={dateTime}
                      onChangeDate={(value) => setDateTime(value)}
                      helperText={""}
                    />
                  </Box>

                  {/* CP Site Visit Photo */}
                  <Box margin={"2.3rem 0"}>
                    <Box className={cssClasses.InputTitle} mb={2}>
                      CP Site Visit Photo
                      <span className={cssClasses.Star}>*</span>
                    </Box>
                    <Box>
                      <CameraCapture
                        CapturePhoto={() => setWebCamModalToggler(true)}
                        WebCamOpen={webCamModalToggler}
                        HandleCaptureClose={() => setWebCamModalToggler(false)}
                        InitialData={imageBase64}
                        UploadedData={(_v) => setImageBase64(_v)}
                        SaveData={SavingImageData}
                        siteVist={true}
                        uploadPhotoAccess={uploadPhotoAccess}
                      />
                    </Box>
                  </Box>


                   <Box margin={"2rem 0"}>
                    <Box className={cssClasses.InputTitle} mb={1}>
                      Select Mode <span className={cssClasses.Star}>*</span>
                    </Box>
                    <SearchSelect
                      options={MODE_OPTIONS}
                      value={selectedMode}
                      onChange={(value) => {
                        setSelectedMode(value);
                        setModeError("");
                      }}
                      placeholder="Select Mode"
                      helperText={modeError}
                    />
                  </Box>

                  {/* Site Visit Remarks */}
                  <Box margin={"2rem 0"}>
                    <Box className={cssClasses.InputTitle}>
                      Site Visit Remarks
                    </Box>
                    <TextField
                      maxRows={4}
                      value={remark}
                      onChange={(e) => {
                        e.target.value = e.target.value.trimStart();
                        if (e.target.value.length <= 200) {
                          setRemark(e.target.value);
                        }
                      }}
                      placeholder={"Type..."}
                      fullWidth
                      multiline
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          background: "#F2F4F7",
                          borderRadius: "1rem",
                          fontSize: "1.6rem",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid #F2F4F7 !important",
                        },
                        "& textarea": { color: "#101828" },
                      }}
                    />
                  </Box>

              
              

                </Box>
              )}

              {/* ---------------- STEP 1 ---------------- */}
              {activeStep === 1 && (
                <>
                  <Box mt={4}>
                    <Box className={cssClasses.flexRow1}>
                      <Box width={"fit-Content"}>
                        <Button
                          style={{
                            fontSize: "1.6rem",
                            lineHeight: "2.4rem",
                            color: "#000000",
                            backgroundColor: "#FFFFFF",
                            border: "none",
                            boxShadow: "none",
                          }}
                          padding={"0"}
                          btnIcon={
                            <ArrowBack
                              style={{
                                width: "1.9rem",
                                height: "1.9rem",
                                color: "#000000",
                                marginTop: "0.5rem",
                              }}
                            />
                          }
                          iconPosition={"left"}
                          btnName={""}
                          clicked={() => goBack()}
                        />
                      </Box>
                      <Box className={cssClasses.title}>Verification</Box>
                    </Box>

                    <Box className={cssClasses.SecondStepper}>
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        flexWrap={"nowrap"}
                        gap={"2rem"}
                      >
                        <Box mt={2}>
                          <Box style={{ position: "relative" }}>
                            <Box className={cssClasses.InputTitle} mb={1}>
                              Phone Number{" "}
                              <span className={cssClasses.Star}>*</span>
                            </Box>
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              gap={"1.2rem"}
                              flexWrap={"wrap"}
                            >
                              <Box width={"fit-Content"}>
                                <PhoneInput
                                  defaultCountry="IN"
                                  value={`${contactNumber}`}
                                  disabled={
                                    showOtp ||
                                    verifiedPhoneNumber ||
                                    resendButton
                                  }
                                  placeholder="Enter phone number"
                                  onChange={(phone) => {
                                    if (phone !== undefined) {
                                      if (phone.length >= 1) {
                                        if (
                                          isValidPhoneNumber(phone) === false
                                        ) {
                                          setHelperPhone(
                                            "Please Enter valid Phone Number",
                                          );
                                          setShowOtp(false);
                                          setVerifiedPhoneNumber(false);
                                        } else {
                                          setHelperPhone("");
                                          IsNumberBlackListed(phone);
                                        }
                                      }
                                    } else {
                                      setHelperPhone(
                                        "Please Enter Phone Number",
                                      );
                                      setShowOtp(false);
                                      setVerifiedPhoneNumber(false);
                                    }
                                    setContactNumber(phone || "");
                                  }}
                                />
                                <p
                                  style={{
                                    color: "red",
                                    fontSize: "1.4rem",
                                    lineHeight: "1.66",
                                  }}
                                >
                                  {helperPhone}
                                </p>
                              </Box>

                              <Box>
                                {helperPhone === "" &&
                                  verifiedPhoneNumber === false &&
                                  otptimer === false &&
                                  showOtp === false &&
                                  contactNumber !== "" && (
                                    <Box
                                      className={cssClasses.verifyOtp}
                                      onClick={() => {
                                        setShowOtp(true);
                                        smsApiCall();
                                      }}
                                    >
                                      Verify Otp
                                    </Box>
                                  )}
                                {verifiedPhoneNumber === false &&
                                  showOtp === true &&
                                  resendButton === true && (
                                    <div
                                      className={cssClasses.verifyOtp}
                                      onClick={() => {
                                        setHelperPhone("");
                                        smsApiCall();
                                      }}
                                    >
                                      Resend Otp
                                    </div>
                                  )}
                                {verifiedPhoneNumber === false &&
                                  showOtp === true &&
                                  resendButton === false && (
                                    <div className={cssClasses.resendOtp}>
                                      {" "}
                                      Resend Otp in{" "}
                                      <CountdownTimer
                                        startFrom={180}
                                        onComplete={() => setResendButton(true)}
                                        timeFormatter={(time) => (
                                          <span>
                                            {`0${Math.floor(time / 60)}`.slice(
                                              -2,
                                            )}
                                            :{`0${time % 60}`.slice(-2)}
                                          </span>
                                        )}
                                      />
                                    </div>
                                  )}
                                {helperPhone === "" &&
                                  verifiedPhoneNumber &&
                                  otptimer === false && (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <CheckCircleSharpIcon
                                        style={{
                                          fontSize: "2.5rem",
                                          marginRight: "1rem",
                                        }}
                                        color={"success"}
                                      />
                                    </div>
                                  )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* OTP boxes */}
                        <Box
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Box
                            className={cssClasses.UnitDiv}
                            width={"50%"}
                            margin={"2rem 0"}
                          >
                            {showOtp && (
                              <Box>
                                <Box className={cssClasses.InputTitle} mb={1}>
                                  OTP sent to contact:
                                </Box>
                                <Box className={cssClasses.flexRow}>
                                  <input
                                    className={cssClasses.otpInput}
                                    name="otp1"
                                    id="opt-0"
                                    type="text"
                                    autoComplete="off"
                                    value={OtpNumber.otp1}
                                    onChange={(e) => optChange("otp1", e)}
                                    tabIndex="0"
                                    maxLength="1"
                                    onKeyUp={(e) => inputfocus(e)}
                                  />
                                  <input
                                    name="otp2"
                                    id="opt-1"
                                    type="text"
                                    autoComplete="off"
                                    className={cssClasses.otpInput}
                                    value={OtpNumber.otp2}
                                    onChange={(e) => optChange("otp2", e)}
                                    tabIndex="1"
                                    maxLength="1"
                                    onKeyUp={(e) => inputfocus(e)}
                                  />
                                  <input
                                    name="otp3"
                                    id="opt-2"
                                    type="text"
                                    autoComplete="off"
                                    className={cssClasses.otpInput}
                                    value={OtpNumber.otp3}
                                    onChange={(e) => optChange("otp3", e)}
                                    tabIndex="2"
                                    maxLength="1"
                                    onKeyUp={(e) => inputfocus(e)}
                                  />
                                  <input
                                    name="otp4"
                                    type="text"
                                    id="opt-3"
                                    autoComplete="off"
                                    className={cssClasses.otpInput}
                                    value={OtpNumber.otp4}
                                    onChange={(e) => optChange("otp4", e)}
                                    tabIndex="3"
                                    maxLength="1"
                                    onKeyUp={(e) => inputfocus(e)}
                                  />
                                </Box>
                                <Box width={"fit-Content"} mt={2}>
                                  <Button
                                    tabIndex={4}
                                    style={{
                                      fontSize: "1.6rem",
                                      lineHeight: "2.4rem",
                                      color: "#ffffff",
                                      backgroundColor: "#155EEF",
                                      borderRadius: "1rem",
                                    }}
                                    padding={"0.8rem 1.1rem"}
                                    btnName={"Verify OTP"}
                                    clicked={submitOtp}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Start / End time */}
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        gap={"2rem"}
                        mt={4}
                      >
                        <Box>
                          <Box className={cssClasses.InputTitle}>
                            Start Time<span className={cssClasses.Star}>*</span>
                          </Box>
                          <EndTimePicker
                            value={siteVisitStart}
                            helperText={dateTimeError.start}
                            onChangeTime={(value) => {
                              if (!value) {
                                setSiteVisitStart(null);
                              } else {
                                const picked = dayjs(value);
                                const todayWithTime = dayjs()
                                  .hour(picked.hour())
                                  .minute(picked.minute())
                                  .second(0)
                                  .millisecond(0);
                                setSiteVisitStart(todayWithTime.toDate());
                              }
                              setDateTimeError((pre) => ({
                                ...pre,
                                start: "",
                              }));
                            }}
                          />
                        </Box>
                        <Box>
                          <Box className={cssClasses.InputTitle}>
                            End Time<span className={cssClasses.Star}>*</span>
                          </Box>
                          <EndTimePicker
                            value={siteVisitEnd}
                            helperText={dateTimeError.end}
                            onChangeTime={(value) => {
                              if (!value) {
                                setSiteVisitEnd(null);
                              } else {
                                const picked = dayjs(value);
                                const todayWithTime = dayjs()
                                  .hour(picked.hour())
                                  .minute(picked.minute())
                                  .second(0)
                                  .millisecond(0);
                                setSiteVisitEnd(todayWithTime.toDate());
                              }
                              setDateTimeError((pre) => ({ ...pre, end: "" }));
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>

          {/* Footer */}
          <Box className={cssClasses.FooterDiv}>
            <Box className={cssClasses.flexRow}>
              <Box width={"fit-Content"} ml={"auto"}>
                <Button
                  style={{
                    color: "#344054",
                    backgroundColor: "#ffffff",
                    fontSize: "1.6rem",
                    lineHeight: "2.4rem",
                  }}
                  padding={"0.85rem 2.6rem"}
                  btnName={"Cancel"}
                  clicked={CancelClose}
                />
              </Box>
              <Box width={"fit-Content"}>
                {activeStep === 0 && (
                  <Button
                    style={{
                      fontSize: "1.6rem",
                      lineHeight: "2.4rem",
                      color: "#FFF",
                      backgroundColor: "#155EEF",
                    }}
                    padding={"0.85rem 3.2rem"}
                    btnName={"Next"}
                    clicked={NextStep}
                  />
                )}
                {activeStep === 1 && (
                  <Button
                    style={{
                      fontSize: "1.6rem",
                      lineHeight: "2.4rem",
                      color: "#ffffff",
                      backgroundColor: "#155EEF",
                    }}
                    disable={verified === false || submitting}
                    btnName={submitting ? "Saving..." : "Save"}
                    clicked={OnSubmit}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

export default AddCpSiteVisit;
